import * as Sentry from '@sentry/react';
import { doc, Firestore, getDocFromServer } from 'firebase/firestore';

// v2 parity (enabledFeatures.firestore_accessible_check, dropped in the rewrite):
// same probe doc path the legacy check read after sign-in.
export const FIRESTORE_ACCESS_PROBE_PATH = ['test', 'testDoc'] as const;
/**
 * 10s, deliberately short (dev decision 2026-08-02): the probe is just a
 * TRIGGER for the iPad cases where authentication completed but the Firestore
 * client's setup didn't come up correctly behind it (AAC blackhole, dead
 * pooled connection after background, SDK stuck in offline state). Its job is
 * to force that first round-trip early so a definite `unavailable` surfaces
 * as FS-01 within seconds — NOT to be a reliable reachability verdict. A
 * timeout here therefore does not block boot (a slow start's client init
 * routinely outruns 10s and the exam would have worked), so there is nothing
 * to gain from waiting longer: 10s caps the worst-case boot delay the trigger
 * can add while still giving the SDK time to return its own answer.
 */
export const FIRESTORE_ACCESS_PROBE_TIMEOUT_MS = 10_000;
export const FIRESTORE_ACCESS_ERROR_NAME = 'FirestoreAccessError';

const inaccessibleError = (message: string, cause?: unknown, code?: string): Error => {
  const error = new Error(message, cause !== undefined ? { cause } : undefined);
  error.name = FIRESTORE_ACCESS_ERROR_NAME;
  if (code) (error as Error & { code?: string }).code = code;
  return error;
};

/**
 * One bounded post-sign-in accessibility probe: a server read of the v2 parity
 * doc raced against a 10s deadline. Sign-in success does not prove Firestore's
 * data channels are reachable (iPad AAC lockdown allowlists can pass auth but
 * blackhole Firestore).
 *
 * The ENTIRE outcome decision lives here (dev review 2026-08-02 — no
 * throw/re-triage split with the caller):
 *  - read resolves → accessible, returns.
 *  - `unavailable` — the SDK's definite "client is offline" /
 *    server-unreachable answer — THROWS `FirestoreAccessError` (code on the
 *    error); the realtime provider's catch turns it into FS-01.
 *  - EVERYTHING else resolves after a Sentry WARNING (FirestoreAccessError
 *    name/message preserved → same issue grouping): a rules denial is the
 *    multi-tab stale-primary transient the real reads self-heal past; the 10s
 *    race timeout is usually CLIENT INIT outrunning the bound on a slow
 *    start, not the wire — killing boot there failed starts that would have
 *    succeeded.
 */
export const probeFirestoreAccess = async (db: Firestore): Promise<void> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        inaccessibleError(
          `Firestore accessibility probe timed out after ${FIRESTORE_ACCESS_PROBE_TIMEOUT_MS}ms`
        )
      );
    }, FIRESTORE_ACCESS_PROBE_TIMEOUT_MS);
  });

  try {
    await Promise.race([
      getDocFromServer(doc(db, FIRESTORE_ACCESS_PROBE_PATH[0], FIRESTORE_ACCESS_PROBE_PATH[1])),
      deadline,
    ]);
  } catch (error: any) {
    if (error?.code === 'unavailable') {
      // Definite can't-reach → the only outcome that blocks boot (FS-01).
      throw inaccessibleError(
        `Firestore accessibility probe failed: ${error.code}`,
        error,
        error.code
      );
    }
    // Non-blocking outcome (timeout / denial / other) — warn and let boot
    // proceed. The race-timeout error is already a FirestoreAccessError;
    // everything else gets wrapped so Sentry grouping stays uniform.
    const warning =
      error?.name === FIRESTORE_ACCESS_ERROR_NAME
        ? error
        : inaccessibleError(
            `Firestore accessibility probe failed: ${
              error?.code ?? error?.message ?? 'network error'
            }`,
            error,
            error?.code
          );
    Sentry.captureException(warning, { level: 'warning' });
  } finally {
    clearTimeout(timeoutId);
  }
};
